// Cel-shading core for WORLD COSTA BLANCA.
//
// SOURCE: donor technique from sakura-crossing src/core/toon.js (REPURPOSE —
//   the gradient-ramp + hue-shifted-shadow method, not the Japanese identity).
// ADAPTATION (2026-08-19, Kimi K3): default shadow tint is Mediterranean
//   blue-mauve (Sorolla colored shadows), ramps biased high-key so whitewashed
//   facades stay luminous even in shade.
//
// Everything opaque in the world uses MeshToonMaterial with a hand-authored
// gradient ramp, so sunlight is quantised into flat bands. The toon BRDF is
// patched so darker bands are TINTED toward blue-mauve instead of going grey —
// that hue shift is the Sorolla "colored shadows" principle in 3D.

import * as THREE from 'three';

const RAMPS = {
  2: [104, 255],
  3: [96, 182, 255],
  4: [86, 148, 208, 255],
  // high-key ramps: limewash whites and pale masses that must stay light
  soft: [178, 255],
  soft3: [164, 214, 255]
};

const rampCache = new Map();

export function gradientMap(bands = 3) {
  if (rampCache.has(bands)) return rampCache.get(bands);
  const stops = RAMPS[bands] || RAMPS[3];
  const data = new Uint8Array(stops.length * 4);
  for (let i = 0; i < stops.length; i += 1) {
    data[i * 4] = stops[i];
    data[i * 4 + 1] = stops[i];
    data[i * 4 + 2] = stops[i];
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, stops.length, 1, THREE.RGBAFormat);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  rampCache.set(bands, tex);
  return tex;
}

const TOON_CHUNK = 'lights_toon_pars_fragment';
const TOON_LINE = 'vec3 irradiance = getGradientIrradiance( geometryNormal, directLight.direction ) * directLight.color;';
const TOON_PATCH = `
	vec3 celBand = getGradientIrradiance( geometryNormal, directLight.direction );
	vec3 irradiance = celBand * mix( uShadowTint, vec3( 1.0 ), celBand ) * directLight.color;`;

let patchAvailable = false;
let patchedChunk = '';
{
  const src = THREE.ShaderChunk[TOON_CHUNK];
  if (src && src.includes(TOON_LINE)) {
    patchedChunk = 'uniform vec3 uShadowTint;\n' + src.replace(TOON_LINE, TOON_PATCH);
    patchAvailable = true;
  }
}

/** Tint the shadow side of a toon material toward a cool hue. */
function applyShadowTint(mat, tint) {
  if (!patchAvailable) return mat;
  const uni = { value: new THREE.Color(tint) };
  mat.userData.shadowTint = uni;
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uShadowTint = uni;
    shader.fragmentShader = shader.fragmentShader.replace(`#include <${TOON_CHUNK}>`, patchedChunk);
  };
  const hex = new THREE.Color(tint).getHexString();
  mat.customProgramCacheKey = () => `celTint_${hex}`;
  return mat;
}

// Mediterranean colored shadow: warm sunlit surfaces fall into blue-mauve
// shade, never into grey/black (Sorolla luminism, docs/04).
export const SHADOW_TINT = 0x8d82ac;

const matCache = new Map();

/** Cel-shaded material factory. Cached by signature so the world shares programs. */
export function cel(opts = {}) {
  const {
    color = 0xffffff,
    bands = 3,
    tint = SHADOW_TINT,
    flat = true,
    map = null,
    emissive = null,
    emissiveIntensity = 1,
    transparent = false,
    opacity = 1,
    side = THREE.FrontSide,
    vertexColors = false,
    cache = true
  } = opts;

  const key = cache && !map
    ? [color, bands, tint, flat, emissive, emissiveIntensity, transparent, opacity, side, vertexColors].join('|')
    : null;
  if (key && matCache.has(key)) return matCache.get(key);

  const mat = new THREE.MeshToonMaterial({
    color,
    gradientMap: gradientMap(bands),
    flatShading: flat,
    map,
    transparent,
    opacity,
    side,
    vertexColors,
    emissive: emissive === null ? 0x000000 : emissive,
    emissiveIntensity
  });
  applyShadowTint(mat, tint);
  if (key) matCache.set(key, mat);
  return mat;
}

const flatCache = new Map();

/** Unlit flat colour — sky elements, glass fills, foam, glows, silhouettes. */
export function flat(opts = {}) {
  const {
    color = 0xffffff,
    map = null,
    transparent = false,
    opacity = 1,
    side = THREE.FrontSide,
    depthWrite = null,
    fog = true,
    cache = true,
    toneMapped = true,
    blending = null,
    vertexColors = false
  } = opts;
  const key = cache && !map
    ? [color, transparent, opacity, side, depthWrite, fog, toneMapped, blending, vertexColors].join('|')
    : null;
  if (key && flatCache.has(key)) return flatCache.get(key);
  const mat = new THREE.MeshBasicMaterial({ color, map, transparent, opacity, side, fog, toneMapped, vertexColors });
  if (depthWrite !== null) mat.depthWrite = depthWrite;
  if (blending !== null) mat.blending = blending;
  if (key) flatCache.set(key, mat);
  return mat;
}
